import { ButtonProps, useDisclosure } from '@chakra-ui/react';
import { FunctionComponent as FC } from 'react';
import { useGetGatewayCustomerAccount } from '../../../api/paymentsService';
import {
  MButton,
  MCenterModal,
  MFormField,
  MGrid,
  MGridItem,
  MPageLoader,
  MStack,
  MText,
} from '../../../components/Monetize';
import { DateDisplay } from '../../../components/Monetize/DateDisplay';
import { PAYMENT_GATEWAY_TYPE_DISPLAY } from '../../../constants/paymentGateWay';
import { GatewayAccountUnlinkModal } from './GatewayAccountUnlinkModal';

interface GatewayAccountModalProps {
  accountId: string;
  paymentGatewayId: string;
  gatewayCustomerId: string;
  buttonProps?: ButtonProps;
  isLoading: boolean;
  isOpen: boolean;
  onOpen: () => void;
  onClose: () => void;
}

export const GatewayAccountModal: FC<GatewayAccountModalProps> = ({
  accountId,
  paymentGatewayId,
  gatewayCustomerId,
  buttonProps,
  isLoading,
  isOpen,
  onOpen,
  onClose,
}: GatewayAccountModalProps) => {
  const {
    open: isUnlinkModalOpen,
    onOpen: onUnlinkModalOpen,
    onClose: onUnlinkModalClose,
  } = useDisclosure();

  const { data, isLoading: gatewayCustomerDataLoading } =
    useGetGatewayCustomerAccount(paymentGatewayId, gatewayCustomerId, {
      enabled: isOpen,
      meta: { showErrorToast: true },
    });

  const handleClose = () => {
    onClose();
  };

  const actions = [
    {
      enabled: true,
      title: 'Unlink Payment Gateway Account',
      action: () => onUnlinkModalOpen(),
    },
  ];

  return (
    <>
      <MButton onClick={onOpen} {...buttonProps}>
        View Payment Account
      </MButton>
      {isOpen && (
        <MCenterModal
          isOpen={isOpen}
          onClose={handleClose}
          modalTitle={'Payment Account Details'}
          size="sm"
          modalHeaderProps={{ display: 'flex', alignItems: 'center' }}
          renderFooter={() => (
            <MStack
              gap={4}
              direction="row"
              align="center"
              justify="space-between"
              flex={1}
            >
              <MButton
                onClick={onUnlinkModalOpen}
                variant="tertiary"
                minW="auto"
                isDisabled={gatewayCustomerDataLoading}
              >
                Unlink
              </MButton>
              <MButton
                onClick={handleClose}
                variant="cancel"
                minW="auto"
                isDisabled={gatewayCustomerDataLoading}
              >
                Close
              </MButton>
            </MStack>
          )}
        >
          <MGrid templateColumns="repeat(12, 1fr)" gap={4}>
            {data ? (
              <>
                <MGridItem colSpan={6}>
                  <MFormField label="Gateway Account ID">
                    <MText mt=".3rem">{data.gatewayAccount.id}</MText>
                  </MFormField>
                </MGridItem>
                <MGridItem colSpan={6}>
                  <MFormField label="Account Name">
                    <MText mt=".3rem">{data.gatewayAccount.name}</MText>
                  </MFormField>
                </MGridItem>
                {data.gatewayAccount.email && (
                  <MGridItem colSpan={12}>
                    <MFormField label="Email">
                      <MText mt=".3rem">{data.gatewayAccount.email}</MText>
                    </MFormField>
                  </MGridItem>
                )}
                <MGridItem colSpan={12}>
                  <MFormField label="Payment Gateway">
                    <MText mt=".3rem">
                      {PAYMENT_GATEWAY_TYPE_DISPLAY[data.type]}
                    </MText>
                  </MFormField>
                </MGridItem>
                <MGridItem colSpan={6}>
                  <MFormField label="Created">
                    <DateDisplay
                      date={data.createDate}
                      timezoneConfigKey="userTimezone"
                      textProps={{ mt: '.3rem' }}
                    />
                  </MFormField>
                </MGridItem>
                <MGridItem colSpan={6}>
                  <MFormField label="Last Modified">
                    <DateDisplay
                      date={data.modifyDate}
                      timezoneConfigKey="userTimezone"
                      textProps={{ mt: '.3rem' }}
                    />
                  </MFormField>
                </MGridItem>
              </>
            ) : (
              <MGridItem colSpan={12}>
                <MPageLoader height={20} />
              </MGridItem>
            )}
          </MGrid>
        </MCenterModal>
      )}
      {isUnlinkModalOpen && (
        <GatewayAccountUnlinkModal
          accountId={accountId}
          paymentGatewayId={paymentGatewayId}
          gatewayCustomerId={gatewayCustomerId}
          onClose={onUnlinkModalClose}
        />
      )}
    </>
  );
};
